import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { getUploadUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowLeft, FileText, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";

export default function PackingList() {
  const { id: batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCliente = user?.role === "cliente";
  const printRef = useRef();
  const [batch, setBatch] = useState(null);
  const [product, setProduct] = useState(null);
  const [empresaActiva, setEmpresaActiva] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPdf, setSavingPdf] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [batchRes, empresaRes] = await Promise.all([
          api.get(`/batches/${batchId}`),
          api.get("/config/empresa-activa")
        ]);
        
        setBatch(batchRes.data);
        setEmpresaActiva(empresaRes.data.empresa);
        
        if (batchRes.data.producto_id) {
          const productRes = await api.get(`/products/${batchRes.data.producto_id}`);
          setProduct(productRes.data);
        }
      } catch (error) {
        toast.error("Error al cargar los datos del lote");
        navigate("/batches");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [batchId, navigate]);

  // Procesar tallas agrupando por talla y desglosando por peso de empaque
  const processTallasConDesglose = () => {
    if (!batch?.tallas) return { tallas: [], pesosUnicos: [] };
    
    // Obtener todos los pesos únicos que se usan
    const pesosSet = new Set();
    batch.tallas.forEach(t => {
      if (t.talla && t.num_bultos && t.peso_por_bulto) {
        pesosSet.add(t.peso_por_bulto);
      }
    });
    const pesosUnicos = Array.from(pesosSet).sort((a, b) => a - b);
    
    // Agrupar por talla
    const tallasMap = {};
    batch.tallas.forEach(t => {
      if (t.talla && t.num_bultos) {
        const key = t.talla;
        if (!tallasMap[key]) {
          tallasMap[key] = { 
            talla: key, 
            kilosTotal: 0, 
            bultosTotal: 0,
            desglose: {} // { peso: { bultos, kilos } }
          };
        }
        
        const kilos = t.kilos_empaque || ((t.num_bultos || 0) * (t.peso_por_bulto || 0));
        const peso = t.peso_por_bulto || 0;
        
        tallasMap[key].kilosTotal += kilos;
        tallasMap[key].bultosTotal += t.num_bultos || 0;
        
        // Desglose por peso
        if (peso > 0) {
          if (!tallasMap[key].desglose[peso]) {
            tallasMap[key].desglose[peso] = { bultos: 0, kilos: 0 };
          }
          tallasMap[key].desglose[peso].bultos += t.num_bultos || 0;
          tallasMap[key].desglose[peso].kilos += kilos;
        }
      }
    });
    
    const tallas = Object.values(tallasMap).sort((a, b) => {
      const numA = parseFloat(a.talla) || 0;
      const numB = parseFloat(b.talla) || 0;
      return numA - numB;
    });
    
    return { tallas, pesosUnicos };
  };

  const processTallas = () => {
    if (!batch?.tallas) return [];
    
    const tallasMap = {};
    batch.tallas.forEach(t => {
      if (t.talla && t.num_bultos) {
        const key = t.talla;
        if (!tallasMap[key]) {
          tallasMap[key] = { talla: key, kilos: 0, bultos: 0 };
        }
        // Usar kilos_empaque si existe, sino calcular desde num_bultos * peso_por_bulto
        const kilos = t.kilos_empaque || ((t.num_bultos || 0) * (t.peso_por_bulto || 0));
        tallasMap[key].kilos += kilos;
        tallasMap[key].bultos += t.num_bultos || 0;
      }
    });
    
    return Object.values(tallasMap).sort((a, b) => {
      const numA = parseFloat(a.talla) || 0;
      const numB = parseFloat(b.talla) || 0;
      return numA - numB;
    });
  };

  // Crear array con filas de tallas (máximo 5 filas vacías extra para que quepa en A4)
  const getTallasConFilasVacias = () => {
    const tallasReales = processTallas();
    const filas = [];
    
    // Añadir las tallas reales
    tallasReales.forEach(t => {
      filas.push(t);
    });
    
    // Rellenar con algunas filas vacías (máximo 5 extra o hasta 10 total)
    const MIN_FILAS = Math.max(tallasReales.length + 2, 6);
    const MAX_FILAS = Math.min(tallasReales.length + 5, 10);
    const targetFilas = Math.max(MIN_FILAS, Math.min(MAX_FILAS, 10));
    
    while (filas.length < targetFilas) {
      filas.push({ talla: '', kilos: null, bultos: null, empty: true });
    }
    
    return filas;
  };

  const handlePrint = () => {
    window.print();
  };

  // Generar nombre del archivo según fórmula:
  // Packing List: AAAAMMDD NÚMEROFACTURAORIGEN PACKING LIST CONTENEDOR/AWB
  // Usa la fecha de CREACIÓN del lote (created_at), no la fecha actual
  const generateFileName = () => {
    // Usar fecha de creación del lote
    const fechaLote = batch?.created_at ? new Date(batch.created_at) : new Date();
    const dateStr = fechaLote.toISOString().slice(0,10).replace(/-/g, '');
    const facturaNum = batch?.factura_origen || "";
    const envio = batch?.contenedor || batch?.awb_aereo || "";
    return `${dateStr} ${facturaNum} PACKING LIST ${envio}`.trim();
  };

  // Guardar como PDF con nombre específico
  const handleSavePdf = async () => {
    // Validar que tenemos los datos del lote
    if (!batch || !batch.numero_lote) {
      toast.error("Error: No se encontraron los datos del lote");
      console.error("Datos del lote incompletos:", batch);
      return;
    }
    
    setSavingPdf(true);
    try {
      const filename = generateFileName();
      const element = printRef.current;
      
      if (!element) {
        toast.error("Error: no se encontró el contenido del documento");
        return;
      }

      const opt = {
        margin: [3, 3, 3, 3], // Márgenes reducidos
        filename: `${filename}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 1.5, // Reducido para mejor ajuste
          useCORS: true,
          letterRendering: true,
          logging: false
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: 'avoid-all', avoid: ['tr', 'td', 'th', 'div'] }
      };

      const pdfBlob = await html2pdf().set(opt).from(element).outputPdf('blob');
      
      // Convertir blob a array buffer para poder reutilizarlo
      const pdfArrayBuffer = await pdfBlob.arrayBuffer();
      
      // Función para subir el documento
      const uploadDocument = async (overwrite = false) => {
        const newBlob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
        const formData = new FormData();
        formData.append("file", newBlob, `${filename}.pdf`);
        formData.append("contenedor", batch.contenedor || "");
        formData.append("awb", batch.awb_aereo || "");
        formData.append("bl", batch.bl_maritimo || "");
        formData.append("numero_lote", batch.numero_lote);
        formData.append("tipo_documento", "packing_list");
        formData.append("batch_id", batch.id || batchId);
        formData.append("nombre_archivo", `${filename}.pdf`);
        if (overwrite) {
          formData.append("overwrite", "true");
        }
        
        console.log("Enviando packing list:", {
          batch_id: batch.id || batchId,
          numero_lote: batch.numero_lote,
          contenedor: batch.contenedor,
          overwrite
        });

        return api.post("/documents/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      };

      // Subir y manejar duplicados
      const response = await uploadDocument(false);
      
      if (response.data?.status === 'duplicate') {
        const existingDoc = response.data.document;
        const confirmOverwrite = window.confirm(
          `Ya existe un Packing List para este lote:\n\n` +
          `Archivo: ${existingDoc?.nombre_archivo || 'Packing List'}\n` +
          `Subido por: ${existingDoc?.subido_por_nombre || 'Sistema'}\n\n` +
          `¿Desea sobrescribir el documento existente?`
        );
        
        if (confirmOverwrite) {
          await uploadDocument(true);
          toast.success("Packing List guardado correctamente (sobrescrito)");
        } else {
          toast.info("Operación cancelada");
          setSavingPdf(false);
          return;
        }
      } else {
        toast.success("PDF guardado correctamente");
      }

      // Descargar localmente
      const downloadBlob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(downloadBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generando PDF:", error);
      toast.error("Error al guardar el PDF");
    } finally {
      setSavingPdf(false);
    }
  };

  // Solo crear/registrar el packing list (genera PDF real y lo guarda)
  const handleCreateDocument = async () => {
    // Validar que tenemos los datos del lote
    if (!batch || !batch.numero_lote) {
      toast.error("Error: No se encontraron los datos del lote");
      console.error("Datos del lote incompletos:", batch);
      return;
    }
    
    setSavingPdf(true);
    try {
      const filename = generateFileName();
      const element = printRef.current;
      
      if (!element) {
        toast.error("Error: no se encontró el contenido del documento");
        return;
      }

      const opt = {
        margin: [3, 3, 3, 3], // Márgenes reducidos
        filename: `${filename}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 1.5, // Reducido para mejor ajuste
          useCORS: true,
          letterRendering: true,
          logging: false
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: 'avoid-all', avoid: ['tr', 'td', 'th', 'div'] }
      };

      const pdfBlob = await html2pdf().set(opt).from(element).outputPdf('blob');
      
      // Convertir blob a array buffer para poder reutilizarlo
      const pdfArrayBuffer = await pdfBlob.arrayBuffer();
      
      // Función para subir el documento
      const uploadDocument = async (overwrite = false) => {
        const newBlob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
        const formData = new FormData();
        formData.append('file', newBlob, `${filename}.pdf`);
        formData.append('contenedor', batch.contenedor || '');
        formData.append('awb', batch.awb_aereo || '');
        formData.append('bl', batch.bl_maritimo || '');
        formData.append('numero_lote', batch.numero_lote);
        formData.append('nombre_archivo', `${filename}.pdf`);
        formData.append('tipo_documento', 'packing_list');
        formData.append('batch_id', batch.id || batchId);
        if (overwrite) {
          formData.append('overwrite', 'true');
        }
        
        console.log("Creando packing list:", {
          batch_id: batch.id || batchId,
          numero_lote: batch.numero_lote,
          contenedor: batch.contenedor,
          overwrite
        });

        return api.post('/documents/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      };

      // Subir y manejar duplicados
      const response = await uploadDocument(false);
      
      if (response.data?.status === 'duplicate') {
        const existingDoc = response.data.document;
        const confirmOverwrite = window.confirm(
          `Ya existe un Packing List para este lote:\n\n` +
          `Archivo: ${existingDoc?.nombre_archivo || 'Packing List'}\n` +
          `Subido por: ${existingDoc?.subido_por_nombre || 'Sistema'}\n\n` +
          `¿Desea sobrescribir el documento existente?`
        );
        
        if (confirmOverwrite) {
          await uploadDocument(true);
          toast.success('Packing List creado correctamente (sobrescrito)');
        } else {
          toast.info('Operación cancelada');
        }
      } else {
        toast.success('Packing List creado correctamente');
      }
    } catch (error) {
      console.error("Error creando Packing List:", error);
      toast.error('Error al crear el documento');
    } finally {
      setSavingPdf(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '';
    return new Intl.NumberFormat('es-ES', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(num);
  };

  const tallasReales = processTallas();
  const { tallas: tallasDesglosadas, pesosUnicos } = processTallasConDesglose();
  const filasTallas = getTallasConFilasVacias();
  
  // Usar peso_neto del lote si existe, sino calcular de las tallas
  // Esto evita inconsistencias entre el peso mostrado arriba y el calculado en la tabla
  const totalKilosCalculado = tallasReales.reduce((sum, t) => sum + t.kilos, 0);
  const totalKilos = batch?.peso_neto || totalKilosCalculado;
  const totalBultos = batch?.bultos || tallasReales.reduce((sum, t) => sum + t.bultos, 0);
  
  // Determinar si hay múltiples pesos de empaque
  const hayMultiplesPesos = pesosUnicos.length > 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Lote no encontrado</p>
      </div>
    );
  }

  const envioId = batch.contenedor || batch.awb_aereo || batch.bl_maritimo || '';

  return (
    <div className="packing-page" data-testid="packing-list-page">
      {/* Header con botones - NO SE IMPRIME */}
      <div className="no-print flex items-center justify-between p-4 bg-white border-b">
        <Button variant="ghost" onClick={() => navigate("/batches")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Lotes
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSavePdf} disabled={savingPdf}>
            {savingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Guardar PDF
          </Button>
          {/* Crear Packing List - Solo admin y proveedor */}
          {!isCliente && (
            <Button onClick={handleCreateDocument} className="bg-slate-900 hover:bg-slate-800" disabled={savingPdf}>
              {savingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Crear Packing List
            </Button>
          )}
        </div>
      </div>

      {/* Contenido del Packing List - FORMATO FIJO A4 COMPACTO */}
      <div 
        ref={printRef} 
        className="packing-content bg-white mx-auto my-4 shadow-md print:shadow-none print:m-0"
        style={{ width: '190mm', maxWidth: '190mm', minHeight: '270mm', padding: '6mm', boxSizing: 'border-box', fontSize: '10px' }}
      >
        {/* Encabezado */}
        <div className="flex justify-between items-start mb-3 pb-2 border-b-2 border-slate-300">
          <div>
            <h1 className="text-xl font-bold text-slate-900">PACKING LIST</h1>
            {envioId && (
              <p className="text-xs text-slate-600">{envioId}</p>
            )}
          </div>
          <div className="flex items-center">
            <img 
              src="/lg_global_logo.png" 
              alt="LG Global Seafood" 
              className="h-10 object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        </div>

        {/* Empresa emisora */}
        <div className="mb-3 pb-2 border-b border-slate-200">
          <h2 className="font-bold text-sm text-slate-800">LG GLOBAL SEAFOOD C.A.</h2>
          <p className="text-xs text-slate-600">
            CALLE REAL DE MONTESANO CARRETERA VIEJA LOCAL GALPON NRO 21 SECTOR MONTESANO MAIQUETIA
          </p>
          <p className="text-xs text-slate-600 font-mono">J-50731006-0</p>
        </div>

        {/* Datos del envío - formato según imagen */}
        <div className="mb-3 text-xs">
          <table className="w-full">
            <tbody>
              <tr>
                <td className="font-bold py-0.5 w-36">CLIENTE:</td>
                <td className="py-0.5">{empresaActiva?.nombre || "EMPRESA"} {empresaActiva?.cif || ""}</td>
              </tr>
              <tr>
                <td className="font-bold py-0.5">ESPECIE:</td>
                <td className="py-0.5">{batch.nombre_cientifico || product?.nombre_cientifico || 'OCTOPUS VULGARIS'}</td>
              </tr>
              <tr>
                <td className="font-bold py-0.5">CONGELACIÓN:</td>
                <td className="py-0.5">PRODUCTO CONGELADO A -21°</td>
              </tr>
              <tr>
                <td className="font-bold py-0.5">DESTINO:</td>
                <td className="py-0.5">ESPAÑA</td>
              </tr>
              <tr>
                <td className="font-bold py-0.5">CODIGO ARANCEL:</td>
                <td className="py-0.5">{batch.codigo_arancelario || product?.codigo_arancelario || '0307.52.00.00'}</td>
              </tr>
              <tr>
                <td className="font-bold py-0.5">TOTAL CAJAS:</td>
                <td className="py-0.5 font-semibold">{batch.bultos || totalBultos}</td>
              </tr>
              <tr>
                <td className="font-bold py-0.5">TOTAL PESO NETO:</td>
                <td className="py-0.5 font-semibold">{formatNumber(totalKilos)}</td>
              </tr>
              <tr>
                <td className="font-bold py-0.5">TOTAL PESO BRUTO:</td>
                <td className="py-0.5 font-semibold">{formatNumber(batch.peso_bruto)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Número de Lote destacado */}
        <div className="bg-slate-800 text-white p-2 text-center mb-3">
          <p className="text-[10px] uppercase tracking-wider">Número de Lote</p>
          <p className="text-lg font-bold">{batch.numero_lote}</p>
        </div>

        {/* Tabla de tallas con desglose por peso de empaque */}
        <div className="mb-3">
          {hayMultiplesPesos ? (
            /* Tabla con desglose de pesos de empaque */
            (() => {
              // Ajustar tamaños según cantidad de pesos
              const muchosPesos = pesosUnicos.length > 4;
              const fontSize = muchosPesos ? '11px' : '14px';
              const headerFontSize = muchosPesos ? '9px' : '11px';
              const cellPadding = muchosPesos ? '3px 1px' : '5px 3px';
              
              return (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize, lineHeight: '1.3' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#334155', color: 'white' }}>
                      <th rowSpan="2" style={{ border: '1px solid #475569', padding: '4px 2px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '600', minWidth: '40px' }}>TALLA</th>
                      <th rowSpan="2" style={{ border: '1px solid #475569', padding: '4px 2px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '600', minWidth: '50px' }}>TOTAL<br/>KILOS</th>
                      <th rowSpan="2" style={{ border: '1px solid #475569', padding: '4px 2px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '600', minWidth: '40px' }}>TOTAL<br/>BULTOS</th>
                      <th colSpan={pesosUnicos.length} style={{ border: '1px solid #475569', padding: '3px', textAlign: 'center', fontWeight: '600' }}>
                        BLOQUES (por peso de empaque)
                      </th>
                    </tr>
                    <tr style={{ backgroundColor: '#475569', color: 'white' }}>
                      {pesosUnicos.map((peso, i) => (
                        <th key={`h-${i}`} style={{ border: '1px solid #64748b', padding: cellPadding, textAlign: 'center', fontSize: headerFontSize, fontWeight: '500' }}>
                          {formatNumber(peso)}kg<br/><span style={{fontSize: '8px'}}>Uds / Kg</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tallasDesglosadas.map((t, idx) => (
                      <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                        <td style={{ border: '1px solid #cbd5e1', padding: cellPadding, fontWeight: '600', textAlign: 'center', verticalAlign: 'middle' }}>
                          {t.talla}
                        </td>
                        <td style={{ border: '1px solid #cbd5e1', padding: cellPadding, textAlign: 'center', fontFamily: 'monospace', fontWeight: '700', backgroundColor: '#e2e8f0' }}>
                          {formatNumber(t.kilosTotal)}
                        </td>
                        <td style={{ border: '1px solid #cbd5e1', padding: cellPadding, textAlign: 'center', fontFamily: 'monospace', fontWeight: '700', backgroundColor: '#e2e8f0' }}>
                          {t.bultosTotal}
                        </td>
                        {pesosUnicos.map((peso, i) => {
                          const desglose = t.desglose[peso];
                          return (
                            <td key={`d-${i}`} style={{ border: '1px solid #cbd5e1', padding: cellPadding, textAlign: 'center', fontFamily: 'monospace' }}>
                              {desglose ? (
                                <span>
                                  <strong>{desglose.bultos}</strong>
                                  <span style={{color: '#64748b', fontSize: '9px'}}> / {formatNumber(desglose.kilos)}</span>
                                </span>
                              ) : '-'}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {/* Fila de totales */}
                    <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: 'bold' }}>
                      <td style={{ border: '1px solid #475569', padding: cellPadding, textAlign: 'center' }}>TOTAL</td>
                      <td style={{ border: '1px solid #475569', padding: cellPadding, textAlign: 'center', fontFamily: 'monospace', fontSize: '13px' }}>
                        {formatNumber(totalKilos)}
                      </td>
                      <td style={{ border: '1px solid #475569', padding: cellPadding, textAlign: 'center', fontFamily: 'monospace', fontSize: '13px' }}>
                        {totalBultos}
                      </td>
                      {pesosUnicos.map((peso, i) => {
                        const totalBultosPeso = tallasDesglosadas.reduce((sum, t) => sum + (t.desglose[peso]?.bultos || 0), 0);
                        const totalKilosPeso = tallasDesglosadas.reduce((sum, t) => sum + (t.desglose[peso]?.kilos || 0), 0);
                        return (
                          <td key={`t-${i}`} style={{ border: '1px solid #475569', padding: cellPadding, textAlign: 'center', fontFamily: 'monospace' }}>
                            {totalBultosPeso > 0 ? (
                              <span>
                                <strong>{totalBultosPeso}</strong>
                                <span style={{fontSize: '9px'}}> / {formatNumber(totalKilosPeso)}</span>
                              </span>
                            ) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              );
            })()
          ) : (
            /* Tabla simple sin desglose */
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '15px', lineHeight: '1.5' }}>
              <thead>
                <tr style={{ backgroundColor: '#334155', color: 'white' }}>
                  <th style={{ border: '1px solid #475569', padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '600', width: '33%' }}>TALLA</th>
                  <th style={{ border: '1px solid #475569', padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '600', width: '33%' }}>KILOS</th>
                  <th style={{ border: '1px solid #475569', padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '600', width: '33%' }}>BULTOS</th>
                </tr>
              </thead>
              <tbody>
                {filasTallas.map((t, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                    <td style={{ border: '1px solid #cbd5e1', padding: '8px 10px', fontWeight: '500', height: '28px', textAlign: 'center', verticalAlign: 'middle' }}>
                      {t.talla || '\u00A0'}
                    </td>
                    <td style={{ border: '1px solid #cbd5e1', padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle', fontFamily: 'monospace' }}>
                      {t.kilos !== null ? formatNumber(t.kilos) : ''}
                    </td>
                    <td style={{ border: '1px solid #cbd5e1', padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle', fontFamily: 'monospace' }}>
                      {t.bultos !== null ? t.bultos : ''}
                    </td>
                  </tr>
                ))}
                {/* Fila de totales */}
                <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: 'bold' }}>
                  <td style={{ border: '1px solid #475569', padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle' }}>TOTAL GENERAL</td>
                  <td style={{ border: '1px solid #475569', padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle', fontFamily: 'monospace' }}>
                    {formatNumber(totalKilos)}
                  </td>
                  <td style={{ border: '1px solid #475569', padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle', fontFamily: 'monospace' }}>
                    {totalBultos}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        {/* Resumen de pesos */}
        <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
          <div className="bg-slate-100 p-2 rounded text-center">
            <p className="text-xs text-slate-500">Total Kilos Netos</p>
            <p className="font-bold text-lg">{formatNumber(totalKilos)} Kg</p>
          </div>
          <div className="bg-slate-100 p-2 rounded text-center">
            <p className="text-xs text-slate-500">Total Kilos Brutos</p>
            <p className="font-bold text-lg">{formatNumber(batch.peso_bruto)} Kg</p>
          </div>
        </div>

        {/* Instrucción de conservación */}
        <div className="bg-green-100 text-center py-2 rounded mb-2">
          <p className="font-bold text-green-800 text-sm">MANTENER CONGELADO A (-18ºC)</p>
        </div>

        {/* Mensaje final */}
        <p className="text-[10px] text-slate-400 text-center">
          Muchas gracias por confiar en nosotros. Pescado de calidad del mar caribe directamente de los pescadores.
        </p>
      </div>

      {/* Estilos para impresión */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 10mm;
          }
          
          /* Forzar colores */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Ocultar elementos no deseados */
          .no-print,
          .print-hide,
          [data-testid="app-sidebar"],
          .sidebar,
          aside {
            display: none !important;
          }
          
          /* El contenedor del packing debe ser visible */
          .packing-page {
            display: block !important;
          }
          
          .packing-content {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
            min-height: auto !important;
          }
          
          /* Asegurar que las tablas se vean bien */
          table {
            width: 100% !important;
            border-collapse: collapse !important;
          }
          
          /* Colores de fondo */
          .bg-slate-700, .bg-slate-800 {
            background-color: #334155 !important;
            color: white !important;
          }
          .bg-slate-100, .bg-slate-50 {
            background-color: #f1f5f9 !important;
          }
          .bg-green-100 {
            background-color: #dcfce7 !important;
          }
        }
      `}</style>
    </div>
  );
}
